/**
 * Given an array to search, the thing you're searching for, and an optional parser, determines if the thing is in the array, and inserts it in the correct spot if it isn't
 * @author Eric Webb <ewebb@factorearth.com>
 * @param arrayToSearch The array we're searching
 * @param thingToSearchFor The element we're searching for (I suppose this won't always be a string, but for my case it is. Change this when it changes)
 * @param compareFunction An optional parser function to be performed on the element before comparison
 */
export function binarySearch<T>(
	arrayToSearch: T[],
	thingToSearchFor: T,
	compareFunction?: (thingToSearchFor: T, itemToCheck: T) => -1 | 0 | 1,
): [boolean, number] {
	let startingIndex = 0;
	let lastIndex = arrayToSearch.length - 1;
	let indexToSearch = 0;
	let lastAction = 0; // We need this guy. If the last element in the array is searched and our thing isn't there, it'll change to be a 1 or -1, indicating which direction it would
	// go, and by extension, we could add or subtract 1 from the indexToSearch to get the index of where it should be
	while (startingIndex <= lastIndex) {
		// Performs a right bit shift. This divides the number by 2, flooring the result. 1101 >> 1 = 0110 (13 >> 1 = 6) because each bit shifts to the right, and the last bit is
		// gone.
		// Since binary searches work by finding the middle point of 2 numbers, we have to modify the startingIndex and lastIndex to keep searching
		// It's an O(1) operation, vs a floor of a divide, which even if it's doing this under the hood, still has extra overhead of being inside another method
		indexToSearch = (startingIndex + lastIndex) >> 1;
		// We have to add or subtract 1 from the result to prevent an infinite loop searching the index at that boundary if the revision would go next to it. Just trust me on this.
		// A better reason is that the 0'th or final'th index could have already been searched, so we remove it to not search it again
		// Reminder in this if/else, 1 means what we're searching for should be at a higher index than what it was compared to. 0 means they'r equal (correct index), -1 means lower
		if (compareFunction) {
			const result = compareFunction(thingToSearchFor, arrayToSearch[indexToSearch]);
			if (result === 0) return [true, indexToSearch];
			if (result === 1) {
				lastAction = 1;
				startingIndex = indexToSearch + 1;
			}
			if (result === -1) {
				lastAction = -1;
				lastIndex = indexToSearch - 1;
			}
		} else {
			if (thingToSearchFor === arrayToSearch[indexToSearch]) return [true, indexToSearch];
			if (thingToSearchFor > arrayToSearch[indexToSearch]) {
				lastAction = 1;
				startingIndex = indexToSearch + 1;
			}
			if (thingToSearchFor < arrayToSearch[indexToSearch]) {
				lastAction = -1;
				lastIndex = indexToSearch - 1;
			}
		}
	}
	// LastAction here is to make sure that whatever just happened, we know that the index should be shifted one place left or right, because to get here, we searched the array and
	// didn't find a match, so we need to take our last index "k" and add or subtract one from it, because regardless of what k is, we know that before inserting the value, k + 1
	// is higher than what we're searching for, while k - 1 is less than what we're searching for, so this is a safe way to insert
	return [false, lastAction === 1 ? indexToSearch + 1 : indexToSearch];
}
